import { body, bodySmall, colorSystem, h4, h5 } from '@luminovo/design-system';
import { styled, TableContainer } from '@mui/material';
import React from 'react';
import { TableComponents } from 'react-virtuoso';

import { TanStackTableContext } from './type';

export const DefaultScroller: TableComponents<unknown, TanStackTableContext<any>>['Scroller'] = React.forwardRef(
    ({ context, ...props }, ref) => {
        switch (context?.size) {
            case 'large':
                return <LargeContainer {...props} ref={ref} />;
            case 'medium':
                return <MediumContainer {...props} ref={ref} />;
            case 'small':
                return <SmallContainer {...props} ref={ref} />;
            default:
                return <MediumContainer {...props} ref={ref} />;
        }
    },
);

const DefaultContainer = styled(TableContainer)(() => ({
    // Allow the container to shrink below the table's intrinsic width, so that wide tables
    // scroll horizontally within the container (TableContainer has `overflow-x: auto`) instead
    // of widening their ancestors until the whole page scrolls.
    minWidth: 0,
    width: 'inherit',
    background: colorSystem.neutral.white,
    border: `1px solid ${colorSystem.neutral[2]}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    '& tr:last-child td': {
        border: 'none',
    },
    '& .MuiTableCell-root': {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 8,
        paddingRight: 8,
        borderBottom: `1px solid ${colorSystem.neutral[2]}`,
        '&:first-of-type': {
            paddingLeft: 16,
        },
        '&:last-child': {
            paddingRight: 16,
        },
    },
    '& .MuiTableRow-root': {
        '&:hover': {
            background: colorSystem.neutral[0],
        },
        // Marks a keyboard user's row. Its own rule: a browser without `:has` drops the whole selector list.
        '&:has(a:focus-visible)': {
            background: colorSystem.neutral[0],
        },
    },
    '& .MuiTableCell-head': {
        color: colorSystem.neutral[8],
        background: colorSystem.neutral[0],
        borderBottom: `1px solid ${colorSystem.neutral[2]}`,
    },
    // NICE TO HAVE: find out why `:horizontal` doesn't work, to style each scrollbar on its own.
    '&::-webkit-scrollbar-track': {
        backgroundColor: colorSystem.neutral[1],
        borderRadius: '0 8px 0px 8px',
    },
    '&::-webkit-scrollbar-corner': {
        backgroundColor: colorSystem.neutral[1],
        borderRadius: '0 0 8px 0',
    },
    '&::-webkit-scrollbar': {
        right: 0,
        width: '10px',
        height: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: colorSystem.neutral[4],
        borderRadius: '8px',
        width: '10px',
        height: '10px',
    },
}));

const headBorder = { borderBottom: `1px solid ${colorSystem.neutral[2]}` };

const LargeContainer = styled(DefaultContainer)(() => ({
    '& .MuiTableCell-head': {
        height: '48px',
        ...h4,
        ...headBorder,
    },
    '& .MuiTableCell-body': {
        height: '56px',
        ...body,
    },
}));

const MediumContainer = styled(DefaultContainer)(() => ({
    '& .MuiTableCell-head': {
        height: '48px',
        ...h5,
        ...headBorder,
    },
    '& .MuiTableCell-body': {
        height: '48px',
        ...bodySmall,
    },
}));

const SmallContainer = styled(DefaultContainer)(() => ({
    '& .MuiTableCell-head': {
        height: '32px',
        ...h5,
        ...headBorder,
    },
    '& .MuiTableCell-body': {
        height: '32px',
        ...bodySmall,
    },
}));
